import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { apiV2, ApiV2Error, type V2PublicInvitation } from '@/lib/api-v2-client'
import { useAuth } from '@/hooks/use-auth'
import { toast } from 'sonner'
import { SiteFooter } from '@/components/site-footer'
import { CheckCircle, XCircle, Loader2, Users } from 'lucide-react'

export const Route = createFileRoute('/invitations/$token')({
  component: AcceptInvitationPage,
  head: () => ({
    meta: [{ title: 'Team Invitation — Uptrack' }],
  }),
})

function AcceptInvitationPage() {
  const { token } = Route.useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [invitation, setInvitation] = useState<V2PublicInvitation | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'accepting' | 'accepted' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState('')

  // Fetch invitation details
  useEffect(() => {
    apiV2.getInvitationByToken(token)
      .then((res) => {
        setInvitation(res.data)
        setStatus('ready')
      })
      .catch((err) => {
        setStatus('error')
        if (err instanceof ApiV2Error && err.status === 404) {
          setErrorMessage('This invitation was not found. It may have been cancelled or already used.')
        } else if (err instanceof ApiV2Error && err.message?.includes('expired')) {
          setErrorMessage('This invitation has expired. Ask the team admin to send a new one.')
        } else {
          setErrorMessage('Something went wrong loading this invitation.')
        }
      })
  }, [token])

  async function handleAccept() {
    setStatus('accepting')
    try {
      const result = await apiV2.acceptInvitation(token)
      if (result.status === 'needs_signup') {
        toast.info('Create an account to join the team.')
        navigate({ to: '/signup', search: { redirect: `/invitations/${token}` } })
        return
      }
      setStatus('accepted')
      toast.success('You have joined the team!')
      setTimeout(() => navigate({ to: '/dashboard' }), 1500)
    } catch (err) {
      setStatus('error')
      if (err instanceof ApiV2Error) {
        setErrorMessage(err.message || 'Failed to accept invitation.')
      } else {
        setErrorMessage('Something went wrong. Please try again.')
      }
    }
  }

  if (authLoading || status === 'loading') {
    return (
      <PageWrapper>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
            <CardTitle className="text-2xl">Loading invitation...</CardTitle>
          </CardHeader>
        </Card>
      </PageWrapper>
    )
  }

  if (status === 'error') {
    return (
      <PageWrapper>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-status-down-soft">
              <XCircle className="h-7 w-7 text-status-down" />
            </div>
            <CardTitle className="text-2xl">Invitation not available</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild className="w-full">
              <Link to="/dashboard">Go to Dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </PageWrapper>
    )
  }

  if (status === 'accepted') {
    return (
      <PageWrapper>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-status-up-soft">
              <CheckCircle className="h-7 w-7 text-status-up" />
            </div>
            <CardTitle className="text-2xl">You're in!</CardTitle>
            <CardDescription>Redirecting to your dashboard...</CardDescription>
          </CardHeader>
        </Card>
      </PageWrapper>
    )
  }

  // status === 'ready' — show invitation details
  const roleLabel = invitation?.role?.replace('_', ' ') ?? ''

  return (
    <PageWrapper>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <Users className="h-7 w-7 text-primary" />
          </div>
          <CardTitle className="text-2xl">Team Invitation</CardTitle>
          <CardDescription>
            You've been invited to join <strong>{invitation?.organization?.name}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Organization</span>
              <span className="text-sm font-medium">{invitation?.organization?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Role</span>
              <Badge variant="outline" className="capitalize">{roleLabel}</Badge>
            </div>
            {invitation?.invited_by && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Invited by</span>
                <span className="text-sm font-medium">{invitation.invited_by.name}</span>
              </div>
            )}
          </div>

          {isAuthenticated ? (
            <Button
              className="w-full"
              onClick={handleAccept}
              disabled={status === 'accepting'}
            >
              {status === 'accepting' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Joining...
                </>
              ) : (
                'Accept Invitation'
              )}
            </Button>
          ) : (
            <div className="space-y-2">
              <Button asChild className="w-full">
                <Link to="/login" search={{ redirect: `/invitations/${token}` }}>Log in to accept</Link>
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link to="/signup" search={{ redirect: `/invitations/${token}` }} className="text-primary hover:underline">
                  Sign up
                </Link>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </PageWrapper>
  )
}

function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 items-start justify-center px-4 py-20">
        {children}
      </div>
      <SiteFooter />
    </div>
  )
}
